import { memo } from 'react';
import { useSelector } from 'react-redux';
import classNames from 'classnames/bind';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircle } from '@fortawesome/pro-regular-svg-icons/faCircle';
import { faCircleDot } from '@fortawesome/pro-solid-svg-icons/faCircleDot';
import { faCheckSquare } from '@fortawesome/pro-regular-svg-icons/faCheckSquare';
import { faSquare } from '@fortawesome/pro-regular-svg-icons/faSquare';
import { faCheck } from '@fortawesome/pro-regular-svg-icons/faCheck';

import usePriceCurrency from '../PriceCurrencyProvider/usePriceCurrency';
import withComponentName from '../../../decorators/withComponentName';
import Tooltip from '../../Tooltip';

import Styles from './styles.module.scss';

const cx = classNames.bind(Styles);

const PricingPlanSubscribeOption = ({
  priceInfo,
  isUserWithPlan,
  text,
  onSubscribe,
  silver,
  basic,
  userPlan,
  gold,
  currentPlan,
  activePlane, activePlaneType, isAddOnActive, addOnType,
  updateParentState, isMobileView, userPlanType, hasInsightsAccess,
}) => {
  const priceOptionClass = cx({
    'price-option-item': true,
    basic,
    silver,
    gold,
    'user-with-plan': isUserWithPlan,
    'guest-user-plan': userPlan === 'guest_plan' && !gold && !silver,
  });
  const { currency, currencySymbol, currencyCode } = usePriceCurrency();

  const isCurrentActive = ((currentPlan === activePlane && priceInfo.plan_type === activePlaneType)
    || isMobileView);

  const handleMainRaidoClick = () => {
    updateParentState('activePlane', currentPlan);
    updateParentState('activePlaneType', priceInfo.plan_type);
  };

  const width = useSelector(({ common }) => (common.width));
  const promobarHeight = useSelector(({ promobar }) => promobar.height);
  const handlePlanRedirect = () => {
    const plansSections = document.getElementById('sub_uk_add_pricing');
    if (!plansSections) return;
    const headerHeight = width > 767 ? 80 : 50;
    const topPosition = plansSections?.getBoundingClientRect()?.top;
    const y = window.pageYOffset + topPosition - headerHeight - 60 - promobarHeight;

    window.scroll({
      top: y,
      behavior: 'smooth',
    });
  };

  const valueSuffix = currency === 'gbp' ? '_gbp' : '';
  const totalMainPrice = priceInfo[`price${valueSuffix}`] + (isAddOnActive ? priceInfo[`add_on_${addOnType}${valueSuffix}`] : 0);
  const totalSubPrice = (priceInfo[`total${valueSuffix}`] || 0) + (isAddOnActive ? priceInfo[`total_${addOnType}${valueSuffix}`] : 0);
  const totalSubText = priceInfo.total_sub_text;

  return (
    <div className={priceOptionClass}>
      <div className={Styles['price-option-item-holder']}>
        {!isUserWithPlan && (
          <FontAwesomeIcon
            className={cx('price-option-item-radio', { active: isCurrentActive })}
            icon={isCurrentActive ? faCircleDot : faCircle}
            onClick={handleMainRaidoClick}
          />
        )}
        {!basic ? (
          <>
            <div className={Styles['price-option-period']}>
              <p>
                <span>
                  {text.replace(/^\w/, (l) => l.toUpperCase())}
                </span>
                &nbsp;
                {priceInfo.billing_period}
                &nbsp;
                {priceInfo.billing_period_unit}
                {priceInfo.billing_period > 1 && 's'}
              </p>
              <div className={Styles['price-option-value']}>
                {currencySymbol}
                {priceInfo[`price${valueSuffix}`]}
                {' '}
                {currencyCode}
                &nbsp;/ month
              </div>
              {userPlan === currentPlan && userPlanType === priceInfo.plan_type && (
                <div className={Styles['price-option-active']}>
                  Subscribed
                  <FontAwesomeIcon
                    className={cx('price-option-active-check')}
                    icon={faCheck}
                  />
                </div>
              )}
            </div>
            <div className={Styles['price-option-btn-block']}>
              <>
                <p className={Styles['price-option-offer']}>
                  {priceInfo.offer}
                </p>
                <div className={Styles['price-sub-info']}>
                  {/* eslint-disable-next-line no-nested-ternary */}
                  {typeof priceInfo.subTextWithPlan === 'string' ? (
                    priceInfo.subTextWithPlan
                      .replace(
                        '{{currencySymbol}}',
                        currencySymbol,
                      )
                      .replace(
                        '{{price}}',
                        priceInfo[`total${valueSuffix}`] || 0,
                      )
                      .replace(
                        '{{currencyCode}}',
                        currencyCode,
                      )
                  ) : (
                    currency === 'gbp'
                      ? priceInfo.subTextWithPlanGbp
                      : priceInfo.subTextWithPlan
                  )}
                </div>
              </>
            </div>
          </>
        ) : (
          <>
            <div className={Styles['price-option-period']}>
              <p>
                <span>
                  BASIC
                </span>
                &nbsp;
              </p>
              <div className={Styles['price-option-value']}>
                Free
              </div>
            </div>
            <div className={Styles['price-option-btn-block']}>
              <div className={Styles['price-sub-info']}>
                Want add ons?
                <br />
                Choose a Premium Plan.
              </div>
            </div>
          </>
        )}
      </div>
      {hasInsightsAccess && !basic && !isUserWithPlan && isCurrentActive && (
        <div className={Styles['price-option-item-add']}>
          <div className={Styles['price-option-item-add-title']}>
            Additional Features and Add Ons
          </div>
          <div className={Styles['price-option-item-add-sub']}>
            <FontAwesomeIcon
              className={cx('price-option-item-add-sub-check', { active: isAddOnActive })}
              icon={isAddOnActive ? faCheckSquare : faSquare}
              onClick={() => updateParentState('isAddOnActive', !isAddOnActive)}
            />
            <span>UK Companies Insights</span>
            <Tooltip
              newIcon
              hasBigScreenDesign
              className={Styles.price_addon_tooltip}
            >
              AI-powered insights drawn from private company filings in the UK.
              <br />
              Only available as an add-on.
              {' '}
              <div className={Styles.price_addon_tooltip_more} onClick={handlePlanRedirect}>
                Learn more
              </div>
            </Tooltip>
          </div>
          {isAddOnActive && (
            <div className={Styles['price-option-item-add-plan']}>
              <div
                className={cx('price-option-item-add-box', { active: addOnType === 'personal' })}
                onClick={() => updateParentState('addOnType', 'personal')}
              >
                <div className={Styles['price-option-item-add-box-title']}>
                  Personal/Startup
                </div>
                <div className={Styles['price-option-item-add-box-sub']}>
                  <span>
                    +
                    {currencySymbol}
                    {priceInfo[`add_on_personal${valueSuffix}`]}
                  </span>
                  {' '}
                  {currencyCode}
                  /month
                </div>
              </div>
              <div
                className={cx('price-option-item-add-box', { active: addOnType === 'enterprises' })}
                onClick={() => updateParentState('addOnType', 'enterprises')}
              >
                <div className={Styles['price-option-item-add-box-title']}>
                  Enterprises
                </div>
                <div className={Styles['price-option-item-add-box-sub']}>
                  <span>
                    +

                    {currencySymbol}
                    {priceInfo[`add_on_enterprises${valueSuffix}`]}
                  </span>
                  {' '}
                  {currencyCode}
                  /month
                </div>
              </div>
            </div>
          )}
        </div>
      )}
      {isMobileView && !isUserWithPlan && (
        <div className={Styles['price-option-btn-holder']}>
          <SubscribeButton
            onSubscribe={onSubscribe}
            silver={silver}
            priceInfo={priceInfo}
          />
          <div className={Styles['price-option-text']}>
            <div className={Styles['price-option-text-main']}>
              {currencySymbol}
              {totalMainPrice || 0}
              {' '}
              {currencyCode}
              /month
            </div>
            {totalSubText && (
              <div className={Styles['price-option-text-sub']}>
                {!!totalSubPrice && (
                  <>
                    {currencySymbol}
                    {totalSubPrice}
                    {' '}
                  </>
                )}
                {totalSubText}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const SubscribeButton = ({ silver, onSubscribe, priceInfo, longTermBtnOption }) => {
  const btnText = silver
    ? 'Start Free Trial'
    : 'Subscribe';
  return (
    <button
      type="button"
      onClick={() => onSubscribe(priceInfo)}
      className={cx('price-option-btn', { 'long-term-option': longTermBtnOption })}
    >
      {btnText}
    </button>
  );
};

export default withComponentName(memo(PricingPlanSubscribeOption));
